import { createApi } from '@reduxjs/toolkit/query/react';
import { ResponseDto, ResponsePagingDto } from 'types';
import {
  ContactCustomerDto,
  ContactDto,
  ContactHistoryDto,
  DeleteContactNoteDto,
  FindAllContactCustomersDto,
  FindAllContactHistoryDto,
  FindAllContactsDto,
  FulltextSearchContactDto,
  UpdateContactDto
} from 'types/dto/contacts';
import { mergeArrays } from 'utils';
import axiosBaseQuery from 'utils/base-api';

export const contactsApi = createApi({
  reducerPath: 'contactsApi',
  tagTypes: [
    'contacts',
    'contact_detail',
    'contact_history',
    'contact_customers',
    'contact_histories_count',
    'contact_customer_count',
    'find_contact_by_chat_session',
    'find_contact_by_chat'
  ],
  baseQuery: axiosBaseQuery,
  endpoints: (builder) => ({
    getContacts: builder.query<ResponsePagingDto<ContactDto>, FindAllContactsDto>({
      query: (params) => ({
        url: '/contacts',
        method: 'get',
        params
      }),
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ contactId }) => ({
              type: 'contacts',
              id: contactId
            }))
          : ['contacts']
    }),

    getContactDetails: builder.query<ResponseDto<ContactDto>, number>({
      query: (contactId) => ({
        url: `/contacts/${contactId}`,
        method: 'get'
      }),
      providesTags: (result) => (result?.data ? [{ type: 'contact_detail', id: result.data.contactId }] : [])
    }),

    getContactDetailByPhoneNumber: builder.query<ResponseDto<ContactDto>, string>({
      query: (phoneNumber) => ({
        url: `/contacts/find_one_by_attribute`,
        method: 'get',
        params: { phoneNumber }
      }),
      providesTags: (result) => (result?.data ? [{ type: 'contact_detail', id: result.data.contactId }] : [])
    }),

    getContactsCount: builder.query<ResponseDto<number>, FindAllContactsDto>({
      query: (params) => ({
        url: '/contacts/count',
        method: 'get',
        params
      }),
      providesTags: (result) => (result?.data ? [{ type: 'contacts', id: 'count' }] : [])
    }),
    getContactsByFullText: builder.query<ResponsePagingDto<ContactDto>, FulltextSearchContactDto>({
      query: (params) => ({
        url: '/contacts/fulltext_search',
        method: 'get',
        params
      }),
      providesTags: (result) =>
        result && result.data?.rows.length > 0
          ? result.data?.rows?.map(({ contactId }) => ({
              type: 'contacts',
              id: contactId
            }))
          : ['contacts']
    }),
    getContactCountByFullText: builder.query<ResponseDto<number>, FulltextSearchContactDto>({
      query: (params) => ({
        url: '/contacts/fulltext_search_count',
        method: 'get',
        params
      }),
      providesTags: (result) => (result?.data ? [{ type: 'contacts', id: 'count' }] : [])
    }),

    getContactHistories: builder.query<ResponsePagingDto<ContactHistoryDto>, FindAllContactHistoryDto>({
      query: ({ contactId, ...params }) => ({
        url: `/contacts/${contactId}/histories`,
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return queryArgs.isMore ? endpointName : endpointName + JSON.stringify(queryArgs);
      },

      merge: (currentCache, newItems, { arg }) => {
        if (arg.pageIndex !== 1 && arg.isMore) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'contactHistoryId');
        } else currentCache.data.rows = newItems.data.rows;
        currentCache.data.count = newItems.data.count;
      },

      providesTags: (result, _, params) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(() => ({
              type: 'contact_history',
              id: params.contactId
            }))
          : ['contact_history']
    }),

    getContactCustomers: builder.query<ResponsePagingDto<ContactCustomerDto>, FindAllContactCustomersDto>({
      query: ({ contactId, ...params }) => ({
        url: `/contacts/${contactId}/customers`,
        method: 'get',
        params
      }),
      providesTags: (result, _, params) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(() => ({
              type: 'contact_customers',
              id: params.contactId
            }))
          : ['contact_customers']
    }),
    updateContactNote: builder.mutation<ResponseDto<ContactDto>, UpdateContactDto>({
      query: ({ contactId, ...updateContact }) => ({
        url: `/contacts/${contactId}/note`,
        method: 'patch',
        data: updateContact
      }),
      invalidatesTags: (_result, _error, param) => [
        { type: 'contacts', id: param.contactId },
        { type: 'contact_detail', id: param.contactId }
      ]
    }),
    getContactsHistoriesCount: builder.query<ResponseDto<number>, FindAllContactHistoryDto>({
      query: ({ contactId, ...params }) => ({
        url: `/contacts/${contactId}/histories/count`,
        method: 'get',
        params
      }),
      providesTags: (result) => (result?.data ? [{ type: 'contact_history', id: 'count' }] : [])
    }),

    getContactsCustomerCount: builder.query<ResponseDto<number>, FindAllContactCustomersDto>({
      query: ({ contactId, ...params }) => ({
        url: `/contacts/${contactId}/customers/count`,
        method: 'get',
        params
      }),
      providesTags: (result) => (result?.data ? [{ type: 'contact_customers', id: 'count' }] : [])
    }),
    deleteContactNote: builder.mutation<ResponseDto<ContactDto>, DeleteContactNoteDto>({
      query: ({ contactId, contactNoteId }) => ({
        url: `/contacts/note/${contactNoteId}`,
        method: 'delete'
      }),
      invalidatesTags: (_result, _error, param) => [
        { type: 'contacts', id: param.contactId },
        { type: 'contact_detail', id: param.contactId }
      ]
    }),
    findContactByChatSession: builder.query<ResponseDto<ContactDto>, number>({
      query: (chatSessionId) => ({
        url: `/contacts/find_one_by_chat_session/${chatSessionId}`,
        method: 'get',
        params: { chatSessionId }
      }),
      providesTags: (res, _, chatSessionId) => [{ type: 'find_contact_by_chat_session', id: chatSessionId }]
    }),

    findContactByChat: builder.query<ResponseDto<ContactDto>, number>({
      query: (chatId) => ({
        url: `/contacts/find_one_by_chat/${chatId}`,
        method: 'get'
        // params: { chatId }
      }),
      providesTags: (res, _, chatId) => [{ type: 'find_contact_by_chat', id: chatId }]
    })
  })
});
export const {
  useGetContactsQuery,
  useGetContactsCountQuery,
  useGetContactsByFullTextQuery,
  useGetContactCountByFullTextQuery,
  useGetContactHistoriesQuery,
  useGetContactDetailsQuery,
  useGetContactDetailByPhoneNumberQuery,
  useGetContactCustomersQuery,
  useLazyGetContactHistoriesQuery,
  useUpdateContactNoteMutation,
  useGetContactsCustomerCountQuery,
  useGetContactsHistoriesCountQuery,
  useDeleteContactNoteMutation,
  useFindContactByChatSessionQuery,
  useFindContactByChatQuery
} = contactsApi;
